import z from "zod";
import {
  BookkeepingAccountIdScheme,
  ProductGroupIdScheme,
  BarcodeFormatIdScheme,
  PrepStationIdScheme,
  CourseIdScheme,
  ModifierGroupIdScheme,
  ModifierIdScheme,
  tagIdScheme,
  ProductIdScheme,
  UnitIdScheme,
  SupplierIdScheme,
} from "./ids";
import { MoneyScheme } from "./money";
import { ProductType } from "./enums/ProductType";
import { ProductVariantScheme } from "./productVariant";
import { BarcodeType } from "./enums/BarcodeType";
import { CatalogOrigin } from "./enums/CatalogOrigin";
import { StockPolicyAssignmentScheme } from "./stockPolicy";

export const ProductDBScheme = z.object({
  id: ProductIdScheme,
  // basic
  name: z.string(),
  unitPrice: MoneyScheme,
  VAT: z.number(),
  unitId: UnitIdScheme.nullable(),
  openPrice: z.boolean(),
  type: z.enum(ProductType),
  groupId: ProductGroupIdScheme.nullable(),

  // bookkeeping
  bookkeepingAccountId: BookkeepingAccountIdScheme.nullable(),
  bookkeepingCostcenter: z.string().optional(),

  // display
  information: z.string().optional(),
  image: z.string().nullable().optional(),

  // retail
  purchasePrice: MoneyScheme.nullable().optional(),
  supplierId: SupplierIdScheme.optional().nullable(),
  barcode: z
    .object({
      code: z.string(),
      type: z.enum(BarcodeType).nullable().default(null),
      format: BarcodeFormatIdScheme.nullable().default(null),
    })
    .optional(),
  stockCount: z.number().nullable().optional(),
  stockPolicy: StockPolicyAssignmentScheme.nullable().optional(),
  shelfLocation: z.string().nullable().optional(),
  serialTracking: z.boolean().nullable().optional(),
  articleNumber: z.string().optional(),
  manufacturerArticleNumber: z.string().optional(),
  manufacturerBarcode: z.string().optional(),
  extraFields: z.record(z.string(), z.string()).nullable().optional(),
  variants: z.array(ProductVariantScheme).optional(),

  // restaurant
  prepStationId: PrepStationIdScheme.optional().nullable(),
  courseId: CourseIdScheme.optional().nullable(),
  requireReference: z.boolean(),
  modifiers: z.array(ModifierIdScheme).optional(),
  modifierGroups: z.array(ModifierGroupIdScheme).optional(),
  alwaysAppliedModifiers: z.array(ModifierIdScheme).optional(),
  takeawayVAT: z.number().optional().nullable(),
  takeawayBookkeepingAccountId:
    BookkeepingAccountIdScheme.optional().nullable(),
  takeawayPrice: MoneyScheme.optional().nullable(),

  // onlineorder
  description: z.string().optional(),
  tags: z.array(tagIdScheme).optional(),

  isDeleted: z.boolean().optional(),
  origin: z.enum(CatalogOrigin).optional(),

  productSyncEnabled: z.boolean().optional(),
  productSyncExternalId: z.string().optional(),
  productSyncExportedAt: z.iso.datetime().optional(),

  noDiscount: z.boolean().optional(),
  externalId: z.string().optional(),
  temporarilyOutOfStock: z.boolean().optional(),

  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});

export const RestGetProduct = ProductDBScheme;

export const RestCreateProduct = ProductDBScheme.omit({
  id: true,
  updatedAt: true,
  createdAt: true,
  isDeleted: true,
  productSyncExportedAt: true,
  temporarilyOutOfStock: true,
});

export const RestUpdateProduct = ProductDBScheme.omit({
  id: true,
  updatedAt: true,
  createdAt: true,
  isDeleted: true,
  productSyncExportedAt: true,
  temporarilyOutOfStock: true,
}).partial();

export const RestSetTemporarilyOutOfStock = z.object({
  outOfStock: z.array(ProductIdScheme),
  available: z.array(ProductIdScheme),
});

export const RestBulkUpdateProducts = z.object({
  updates: z.array(
    z.object({
      productId: ProductIdScheme,
      changes: RestUpdateProduct,
    }),
  ),
});

export const RestBulkUpdateProductsResponse = z.object({
  updated: z.array(RestGetProduct),
  failed: z.array(
    z.object({
      productId: ProductIdScheme,
      error: z.string(),
    }),
  ),
});

export type DBProduct = z.infer<typeof ProductDBScheme>;
export type RestProduct = z.infer<typeof RestGetProduct>;
export type RestCreateProduct = z.infer<typeof RestCreateProduct>;
export type RestUpdateProduct = z.infer<typeof RestUpdateProduct>;
export type RestSetTemporarilyOutOfStock = z.infer<
  typeof RestSetTemporarilyOutOfStock
>;
export type RestBulkUpdateProducts = z.infer<typeof RestBulkUpdateProducts>;
export type RestBulkUpdateProductsResponse = z.infer<
  typeof RestBulkUpdateProductsResponse
>;
